import React, { useState } from 'react';

// Interface for row error data
interface RowError {
  rowIndex: number;
  fieldErrors: Record<string, string>;
  originalRow: any;
}

// Props interface for the ValidationSummary component
interface ValidationSummaryProps {
  validCount: number;
  invalidCount: number;
  invalidData: RowError[];
  className?: string;
  style?: React.CSSProperties;
  maxInitialErrors?: number; // Number of errors to show initially
}

/**
 * ValidationSummary Component
 * 
 * Displays a summary of validation results including:
 * - Count of valid and invalid records
 * - Detailed field errors for invalid records (with expand/collapse functionality)
 * - User guidance for next steps
 */
export const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  validCount,
  invalidCount,
  invalidData,
  className = '',
  style = {},
  maxInitialErrors = 3 // Show only 3 errors initially
}) => {
  // State to control whether all errors are shown or just the first few
  const [showAllErrors, setShowAllErrors] = useState(false);

  // Default styling for the summary container
  const defaultStyle: React.CSSProperties = {
    padding: 10,
    backgroundColor: '#f9fafb',
    borderRadius: 4,
    marginTop: 15,
    fontSize: 14,
    ...style // Allow custom styles to override defaults
  };

  // Determine which errors to display based on current state
  const errorsToShow = showAllErrors 
    ? invalidData 
    : invalidData.slice(0, maxInitialErrors);

  // Check if we have more errors than what we're currently showing
  const hasMoreErrors = invalidData.length > maxInitialErrors;
  const hiddenErrorsCount = invalidData.length - maxInitialErrors;

  return (
    <div className={`validation-summary ${className}`} style={defaultStyle}>
      {/* Main summary information */}
      <div>
        <strong>Samantekt:</strong> {validCount} gildar færslur, {invalidCount} villur
      </div>

      {/* Warning message for invalid data */}
      {invalidCount > 0 && (
        <div style={{ color: "#dc2626", marginTop: 4 }}>Þú þarft að laga villurnar áður en þú getur sent gögnin.</div>
      )}

      {/* Detailed field errors section */}
      {invalidCount > 0 && invalidData.length > 0 && (
        <div style={{ marginTop: 5 }}>
          {/* Display the selected errors */}
          {errorsToShow
            // Sort errors by rowIndex in ascending order (lowest row numbers first)
            .sort((a, b) => a.rowIndex - b.rowIndex)
            .map((errorRow, index) => (
              <div
                key={`error-row-${errorRow.rowIndex}-${index}`}
                style={{
                  marginTop: 3,
                  paddingLeft: 10,
                  borderLeft: "3px solid #dc2626",
                }}
              >
                {/* Row identifier */}
                <div style={{ fontWeight: "bold", fontSize: 13 }}>
                  Röð {errorRow.rowIndex}:{/* Concatenate all field errors into a single string */}
                  <span
                    style={{
                      fontSize: 12,
                      color: "#dc2626",
                      marginLeft: 2,
                    }}
                  >
                    {" "}
                    {Object.values(errorRow.fieldErrors).join(", ")}
                  </span>
                </div>
              </div>
            ))}

          {/* Show/Hide more errors button */}
          {hasMoreErrors && (
            <div style={{ marginTop: 8 }}>
              <span
                style={{ color: "#4F5059", cursor: "pointer", fontSize: 12 }}
                onClick={() => setShowAllErrors(!showAllErrors)}
              >
                {showAllErrors ? "Fela villur" : `Sjá allar villur (+${hiddenErrorsCount} í viðbót)`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Success message when no errors */}
      {invalidCount === 0 && validCount > 0 && (
        <div style={{ color: "#059669", marginTop: 4 }}>Öll gögn eru gild og tilbúin til sendingar.</div>
      )}

      {/* No data message */}
      {validCount === 0 && invalidCount === 0 && (
        <div style={{ color: "#6b7280", marginTop: 4 }}>Engin gögn fundust í skjalinu.</div>
      )}
    </div>
  );
};